import type { ClientListItem } from "@planerius/contracts/cabinet"

/**
 * Черновик быстрой формы записи (2-2.7.1) и всё, что с ним может случиться.
 *
 * ЭТОТ ФАЙЛ СУЩЕСТВУЕТ РАДИ ОДНОГО ОБЕЩАНИЯ: ВВЕДЁННОЕ НЕ ТЕРЯЕТСЯ.
 *
 * Главный сценарий листа «Журнал - П0» — 2-2.10.2 «слот заняли». Администратор держит
 * трубку, нашёл клиента, выбрал услугу, согласовал время — и в эту секунду то же время
 * занимает витрина. Это ШТАТНЫЙ исход, а не сбой: дальше человек выбирает другое время
 * и дожимает ту же запись. Если при этом стирается хоть одно поле, он кладёт трубку
 * и записывает клиента на бумажке — и продукт проигран именно здесь, а не в архитектуре.
 *
 * Поэтому состояние формы разложено на ДВА НЕЗАВИСИМЫХ слоя:
 *
 *   draft — то, что ввёл человек. Меняется ТОЛЬКО его действиями;
 *   conflict / client / submit — то, что рассказал сервер.
 *
 * Ни одно сообщение сервера не имеет права тронуть draft. Единственное исключение
 * названо по имени и проверяется тестом: выбор нового окна (pick-window) меняет ровно
 * три поля — дату, минуту и мастера, — потому что в этом и состоит смысл действия.
 *
 * Отсюда же форма файла: чистый редьюсер без React. Обещание «ничего не потерялось»
 * проверяется сравнением объектов, а не кликами по экрану.
 */

/** Свободное окно: день, минута от начала дня и мастер. Дата нужна для «завтра, 11:00». */
export interface FreeWindow {
  readonly date: string
  readonly minute: number
  readonly employeeId: string
  /** «14:45» либо «Завтра 11:00» — подпись кнопки из макета. */
  readonly label: string
}

/**
 * Кого записываем. Три исхода поиска из макета плюс два служебных.
 *
 * ПОЧЕМУ «НЕСКОЛЬКО» — ОТДЕЛЬНОЕ СОСТОЯНИЕ, А НЕ АВТОПОДСТАНОВКА ПЕРВОГО. У одного
 * номера бывают две карточки: мама и ребёнок (макет form-many). Подставить первую —
 * значит записать девятилетнего Тимофея вместо Марии и узнать об этом на стрижке.
 */
export type ClientLookup =
  /** Номер ещё короткий или пустой: искать нечего. */
  | { readonly kind: "idle" }
  | { readonly kind: "searching" }
  /** Ровно одна карточка — form-found. */
  | { readonly kind: "found"; readonly client: ClientListItem }
  /** Несколько карточек на номер — form-many. */
  | { readonly kind: "many"; readonly options: readonly ClientListItem[] }
  /** Номера в базе нет — form-new: форма превращается в создание клиента. */
  | { readonly kind: "new" }
  /** Поиск не доехал. Ввод при этом остаётся, записать можно как нового. */
  | { readonly kind: "failed"; readonly message: string }

/** Чем закончился поиск свободных окон ПОСЛЕ отказа «слот заняли» (четыре подсостояния). */
export type WindowSearch =
  /** taken-1: каркасные пилюли и «Ищем ближайшие окна…». */
  | { readonly kind: "searching" }
  /** taken-2: окна нашлись, в одно нажатие. */
  | { readonly kind: "found"; readonly windows: readonly FreeWindow[] }
  /** taken-3: у мастера на сегодня пусто; ближайшее — в другой день либо ни одного. */
  | { readonly kind: "none"; readonly next: FreeWindow | null }
  /** taken-4: вторая ошибка поверх первой — окна не подгрузились. */
  | { readonly kind: "failed" }

/**
 * Отказ «это время только что заняли».
 *
 * takenLabel хранится строкой, а не минутой: время в сообщении — то, которое человек
 * ВИДЕЛ, и после выбора нового окна оно меняться не должно («Пока вы оформляли запись,
 * 14:30 у Анны занял другой клиент»).
 */
export interface SlotConflict {
  readonly takenLabel: string
  readonly employeeName: string
  readonly search: WindowSearch
}

export interface BookingDraft {
  /** Календарная дата филиала. В черновике она есть, чтобы «завтра, 11:00» было выразимо. */
  readonly date: string
  readonly startMinute: number
  /** Пусто = «Без мастера»: запись ляжет в колонку неразобранных. */
  readonly employeeId: string | null
  readonly phone: string
  /** Выбранная карточка клиента. Пусто и при новом клиенте, и пока номер не найден. */
  readonly clientId: string | null
  /** Имя со слов клиента — только для нового. */
  readonly clientName: string
  /** Согласие на обработку ПД. Без него нового клиента завести нельзя. */
  readonly consentPersonalData: boolean
  /** Согласие на рекламу — ОТДЕЛЬНЫЙ документ и отдельная отметка, по умолчанию нет. */
  readonly consentAds: boolean
  /**
   * Услуги визита — СПИСОК, а не одна.
   *
   * «Стрижка и борода» — обычный заказ у стойки, и раньше администратор заводил его
   * двумя записями подряд: клиент получал два подтверждения, а салон — две строки
   * в журнале вместо одного визита на час. API принимал до десяти позиций с самого
   * начала (CabinetCreateAppointmentRequest.items), одной услугой ограничивал себя
   * только кабинет.
   *
   * Порядок значим: сервер считает шаги подряд в том порядке, в каком их прислали.
   */
  readonly serviceIds: readonly string[]
  /** Сумма длительностей выбранных услуг. Считает вызывающий: каталог знает он. */
  readonly durationMinutes: number
  readonly comment: string
}

/** Удержание слота: ключ и момент, до которого оно живёт. Отсчёт считает интерфейс. */
export interface DraftHold {
  readonly id: string
  readonly expiresAt: string
}

export interface BookingState {
  readonly draft: BookingDraft
  readonly client: ClientLookup
  readonly conflict: SlotConflict | null
  /**
   * Предупреждения из 422 confirmation_required: их показывают словами и подтверждают
   * осознанно. Тексты приходят от сервера — он один знает правила филиала.
   */
  readonly warnings: readonly WarningNote[]
  readonly hold: DraftHold | null
  readonly submitting: boolean
  /** Отказ, который человеку нужно прочитать. Ввод при этом на месте. */
  readonly failure: string | null
}

/** Предупреждение сервера в том виде, в котором его показывает форма. */
export interface WarningNote {
  readonly code: string
  readonly message: string
}

export interface OpenSlot {
  readonly date: string
  readonly startMinute: number
  readonly employeeId: string | null
  readonly durationMinutes: number
  /**
   * Заготовка для «Продублировать»: тот же клиент, та же услуга, тот же комментарий.
   *
   * В адресе её НЕТ намеренно. Адрес слота отвечает на вопрос «куда записываем»,
   * и по ссылке форма обязана открыться чистой: пересланная коллеге ссылка не должна
   * тянуть за собой чужого клиента. Заготовка живёт ровно один клик.
   */
  readonly prefill?: DraftPrefill | undefined
}

/** Что известно о записи, которую дублируют. */
export interface DraftPrefill {
  readonly client?: ClientListItem | undefined
  readonly serviceIds?: readonly string[] | undefined
  /** Длительность повторяемого визита: шаг сетки для него — чужая мерка. */
  readonly durationMinutes?: number | undefined
  readonly comment?: string | undefined
}

export function openDraft(slot: OpenSlot): BookingState {
  const prefilled = slot.prefill?.client
  return {
    draft: {
      date: slot.date,
      startMinute: slot.startMinute,
      employeeId: slot.employeeId,
      // Телефон берётся маской, а не пустым: по ней человек узнаёт, кого дублирует.
      // Полный номер сюда не попадает — его показ отдельное действие с записью в аудит.
      phone: prefilled?.phoneMasked ?? "",
      clientId: prefilled?.id ?? null,
      clientName: prefilled?.displayName ?? "",
      // Согласие на обработку отмечено заранее — это ОТМЕТКА ФАКТА («получено устно»),
      // которую администратор снимает, если клиент отказался, а не разрешение,
      // которое мы ставим за него. Рекламное согласие по умолчанию НЕТ: это другой
      // документ, и штраф за их объединение платит салон.
      consentPersonalData: true,
      consentAds: false,
      serviceIds: slot.prefill?.serviceIds ?? [],
      // Длительность повторяемого визита сильнее шага сетки: «Продублировать»
      // повторяет час, а не пятнадцатиминутную ячейку, на которую открыли форму.
      durationMinutes: slot.prefill?.durationMinutes ?? slot.durationMinutes,
      comment: slot.prefill?.comment ?? "",
    },
    client: prefilled === undefined ? { kind: "idle" } : { kind: "found", client: prefilled },
    conflict: null,
    warnings: [],
    hold: null,
    submitting: false,
    failure: null,
  }
}

export type BookingEvent =
  // ─── ввод человека ───
  | { readonly type: "phone"; readonly value: string }
  | { readonly type: "client-name"; readonly value: string }
  | { readonly type: "consent-personal"; readonly value: boolean }
  | { readonly type: "consent-ads"; readonly value: boolean }
  /**
   * Новый состав услуг целиком, а не «добавить одну».
   *
   * Редьюсер каталога не знает и длительность сложить не может. Складывает её тот,
   * у кого каталог на руках, — форма; сюда приезжает готовый ответ. Так редьюсер
   * остаётся проверяемым без каталога, а длительность не расходится с составом.
   */
  | {
      readonly type: "services"
      readonly serviceIds: readonly string[]
      readonly durationMinutes: number
    }
  | { readonly type: "employee"; readonly employeeId: string | null }
  | { readonly type: "start-minute"; readonly minute: number }
  /**
   * День визита сменили чипом «Сегодня/Завтра/Дата».
   *
   * ЭТО МЕНЯЕТ ЧЕРНОВИК, а не только список окон. Сначала чип двигал лишь показ —
   * и запись, у которой время поставили вручную, уходила на день журнала: на экране
   * «Завтра», в базе сегодня. Удержание при этом снимается: оно стояло на другом дне
   * и к новому отношения не имеет.
   */
  | { readonly type: "day"; readonly date: string }
  | { readonly type: "comment"; readonly value: string }
  | { readonly type: "choose-client"; readonly client: ClientListItem }
  | { readonly type: "pick-window"; readonly window: FreeWindow }
  // ─── ответы сервера ───
  | { readonly type: "lookup-started" }
  | { readonly type: "lookup-result"; readonly clients: readonly ClientListItem[] }
  | { readonly type: "lookup-failed"; readonly message: string }
  | { readonly type: "hold-set"; readonly hold: DraftHold | null }
  | { readonly type: "submit-started" }
  | { readonly type: "submit-failed"; readonly message: string }
  | { readonly type: "warnings"; readonly warnings: readonly WarningNote[] }
  | { readonly type: "slot-taken"; readonly takenLabel: string; readonly employeeName: string }
  | { readonly type: "windows-searching" }
  | { readonly type: "windows-found"; readonly windows: readonly FreeWindow[] }
  | { readonly type: "windows-none"; readonly next: FreeWindow | null }
  | { readonly type: "windows-failed" }

/**
 * Редакция текста согласия на обработку персональных данных.
 *
 * ЭТО ПРОБЕЛ, А НЕ РЕШЕНИЕ. Версия обязана приходить с сервера: салон меняет текст
 * согласия, и запись «получено устно по редакции от 1 марта» должна ссылаться на ту
 * редакцию, которая действовала в момент разговора, а не на ту, что зашита в кабинет.
 * В контракте AdminConsentCapture поле personalDataVersion обязательное, а маршрута,
 * который отдал бы действующую редакцию, в API нет. До его появления значение живёт
 * здесь, и это вынесено в отчёт.
 */
export const CONSENT_PERSONAL_DATA_VERSION = "2026-03-01"
export const CONSENT_PERSONAL_DATA_LABEL = "редакция от 1 марта 2026"

/** Минимальная длина номера, при которой поиск имеет смысл (макет: «поиск от 4–5 цифр»). */
export const LOOKUP_MIN_DIGITS = 4

export function digitsOf(phone: string): string {
  return phone.replace(/\D/g, "")
}

export function canLookup(phone: string): boolean {
  return digitsOf(phone).length >= LOOKUP_MIN_DIGITS
}

/**
 * Готов ли черновик к отправке.
 *
 * Новый клиент требует имени и согласия на обработку: без них запись создать нельзя
 * ни технически (контракт AdminConsentCapture), ни юридически.
 */
export function canSubmit(state: BookingState): boolean {
  const { draft } = state
  if (draft.serviceIds.length === 0) return false
  if (state.submitting) return false
  if (draft.clientId !== null) return true
  if (state.client.kind !== "new") return false
  return draft.clientName.trim().length > 0 && draft.consentPersonalData
}

/**
 * События, после которых подтверждённые предупреждения больше не про этот заказ.
 *
 * ПОЧЕМУ ЭТО ВАЖНО. Предупреждения подтверждает человек, а уезжают они в запрос
 * списком кодов. Администратор подтвердил «клиент в чёрном списке», передумал,
 * нашёл ДРУГОГО клиента — и подтверждение уехало бы вместе с новым заказом, погасив
 * предупреждение, которого он не видел. То же со временем: «запись в прошлом»,
 * подтверждённая для 9:00, не должна молча подтверждать 21:00.
 */
const CLEARS_WARNINGS: ReadonlySet<BookingEvent["type"]> = new Set([
  "phone",
  "client-name",
  "choose-client",
  "lookup-result",
  "services",
  "employee",
  "start-minute",
  "day",
  "pick-window",
])

export function bookingReducer(state: BookingState, event: BookingEvent): BookingState {
  if (state.warnings.length > 0 && CLEARS_WARNINGS.has(event.type)) {
    return bookingReducer({ ...state, warnings: [] }, event)
  }
  switch (event.type) {
    // ─────────── ввод человека: меняется черновик ───────────
    case "phone": {
      // Смена номера обнуляет ВЫБРАННУЮ карточку, но не остальной ввод: услуга, время
      // и комментарий к номеру отношения не имеют.
      const unchanged = state.draft.phone === event.value
      return {
        ...state,
        draft: {
          ...state.draft,
          phone: event.value,
          clientId: unchanged ? state.draft.clientId : null,
        },
        client: unchanged ? state.client : { kind: "idle" },
        failure: null,
      }
    }
    case "client-name":
      return { ...state, draft: { ...state.draft, clientName: event.value } }
    case "consent-personal":
      return { ...state, draft: { ...state.draft, consentPersonalData: event.value } }
    case "consent-ads":
      return { ...state, draft: { ...state.draft, consentAds: event.value } }
    case "services":
      return {
        ...state,
        draft: {
          ...state.draft,
          serviceIds: event.serviceIds,
          durationMinutes: event.durationMinutes,
        },
      }
    case "employee":
      return { ...state, draft: { ...state.draft, employeeId: event.employeeId } }
    case "start-minute":
      return { ...state, draft: { ...state.draft, startMinute: event.minute } }
    case "day":
      return {
        ...state,
        draft: { ...state.draft, date: event.date },
        hold: null,
      }
    case "comment":
      return { ...state, draft: { ...state.draft, comment: event.value } }
    case "choose-client":
      return {
        ...state,
        draft: { ...state.draft, clientId: event.client.id },
        client: { kind: "found", client: event.client },
      }

    /**
     * ЕДИНСТВЕННОЕ место, где ответ сервера двигает черновик, — и двигает ровно то,
     * ради чего человек нажал кнопку: день, время и мастера. Телефон, клиент, услуга,
     * согласия и комментарий остаются нетронутыми. Отказ при этом снимается: конфликт
     * разрешён, форма снова обычная.
     */
    case "pick-window":
      return {
        ...state,
        draft: {
          ...state.draft,
          date: event.window.date,
          startMinute: event.window.minute,
          employeeId: event.window.employeeId,
        },
        conflict: null,
        // Удержание было поставлено на СТАРОЕ время и больше ничего не держит.
        hold: null,
        failure: null,
      }

    // ─────────── ответы сервера: черновик неприкосновенен ───────────
    case "lookup-started":
      return { ...state, client: { kind: "searching" } }
    case "lookup-result": {
      if (event.clients.length === 0) return { ...state, client: { kind: "new" } }
      const only = event.clients[0]
      if (event.clients.length === 1 && only !== undefined) {
        // Одно совпадение подставляется целиком: администратор набрал номер целиком
        // и знает, кому звонит. Выбирать здесь не из чего.
        return {
          ...state,
          draft: { ...state.draft, clientId: only.id },
          client: { kind: "found", client: only },
        }
      }
      return { ...state, client: { kind: "many", options: event.clients } }
    }
    case "lookup-failed":
      return { ...state, client: { kind: "failed", message: event.message } }
    case "hold-set":
      return { ...state, hold: event.hold }
    case "submit-started":
      return { ...state, submitting: true, failure: null }
    case "submit-failed":
      return { ...state, submitting: false, failure: event.message }
    case "warnings":
      return { ...state, submitting: false, warnings: event.warnings }
    case "slot-taken":
      return {
        ...state,
        submitting: false,
        failure: null,
        conflict: {
          takenLabel: event.takenLabel,
          employeeName: event.employeeName,
          search: { kind: "searching" },
        },
      }
    case "windows-searching":
      return withSearch(state, { kind: "searching" })
    case "windows-found":
      return withSearch(state, { kind: "found", windows: event.windows })
    case "windows-none":
      return withSearch(state, { kind: "none", next: event.next })
    case "windows-failed":
      return withSearch(state, { kind: "failed" })
    default: {
      // Ветка недостижима, но нужна: иначе новый вид события молча ничего не сделает.
      const never: never = event
      return never
    }
  }
}

function withSearch(state: BookingState, search: WindowSearch): BookingState {
  if (state.conflict === null) return state
  return { ...state, conflict: { ...state.conflict, search } }
}

/**
 * Намерение создать ИМЕННО ЭТУ запись — строка для ключа идемпотентности.
 *
 * Меняется вместе с содержимым черновика, и это главное: повтор после обрыва связи
 * обязан попасть в тот же ключ (иначе возникнет второй визит), а запись на ДРУГОЕ время
 * после «слот заняли» обязана получить новый ключ — иначе сервер ответит на неё
 * сохранённым ответом от прошлой попытки, то есть тем самым отказом.
 */
export function draftIntent(draft: BookingDraft): string {
  return [
    "create",
    draft.date,
    String(draft.startMinute),
    draft.employeeId ?? "unassigned",
    // Состав услуг целиком: «стрижка» и «стрижка + борода» — разные визиты,
    // и повтор запроса по одному ключу подменил бы второй первым.
    draft.serviceIds.length === 0 ? "no-service" : draft.serviceIds.join("+"),
    draft.clientId ?? `new:${digitsOf(draft.phone)}:${draft.clientName.trim()}`,
    draft.comment.trim(),
  ].join(":")
}

/** Ключ слота для адреса: день, минута и мастер. Один и тот же слот даёт одну строку. */
export function slotKey(date: string, minute: number, employeeId: string | null): string {
  return `${date}|${String(minute)}|${employeeId ?? "none"}`
}
